import { useEffect, useRef, useState, useCallback } from "react";
import { getPlatformStats, type PlatformStats } from "../../../services/platform";
import RIcon from "../../../components/RIcon";

// ─── Data ────────────────────────────────────────────────────────────────────

function buildFeatureStats(ps: PlatformStats | null) {
  return [
    { end: ps?.indexed_files ?? 0, suffix: "+", label: "文档已索引", icon: "ri-folder-3-line" },
    { end: ps?.total_chats ?? 0, suffix: "+", label: "累计问答", icon: "ri-chat-check-line" },
    { end: ps?.total_users ?? 0, suffix: "+", label: "活跃用户", icon: "ri-user-line" },
    { end: ps?.total_projects ?? 0, suffix: "+", label: "研发项目", icon: "ri-kanban-view" },
  ];
}

const FEATURES = [
  {
    icon: "ri-chat-ai-line",
    title: "AI智能问答",
    desc: "基于企业知识库的专业AI问答，精准引用文件来源，支持Markdown富文本回复",
    gradient: "from-[#0EA5A0] to-[#06B6D4]",
    accentBg: "bg-[#0EA5A0]/10",
    details: [
      "精准引用文件来源，支持原文跳转查看",
      "多轮对话上下文语义连贯理解",
      "回复内容支持Markdown与代码块渲染",
      "覆盖研发 / 临床 / 注册 / 质量全场景",
    ],
  },
  {
    icon: "ri-file-text-line",
    title: "智能文档生成",
    desc: "选择预置模板，填写参数，AI自动生成符合格式规范的专业报告文档",
    gradient: "from-[#10B981] to-[#0EA5A0]",
    accentBg: "bg-emerald-50",
    details: [
      "6 种医疗行业预置专业报告模板",
      "动态参数表单驱动文档内容填充",
      "保留原模板格式与样式排版",
      "一键导出可编辑的 .docx 文件",
    ],
  },
  {
    icon: "ri-shield-check-line",
    title: "多维权限管控",
    desc: "全部 / 部门 / 工种 / 指定人员四级权限体系，精准管控知识访问边界",
    gradient: "from-[#0EA5A0] to-emerald-400",
    accentBg: "bg-teal-50",
    details: [
      "四级权限粒度与组织架构深度绑定",
      "权限变更实时生效，无需重新登录",
      "完整操作日志与审计追踪",
      "支持角色继承与特殊授权组合",
    ],
  },
  {
    icon: "ri-folder-open-line",
    title: "多格式文件管理",
    desc: "支持 PDF、Word、Excel、PPT、音视频等 50+ 种格式，自动 AI 向量索引",
    gradient: "from-[#F59E0B] to-[#EF4444]",
    accentBg: "bg-amber-50",
    details: [
      "音视频文件支持 ASR 自动语音转写",
      "上传后自动触发 AI 向量索引任务",
      "索引状态实时可见，支持手动重建",
      "文件权限继承所属项目配置",
    ],
  },
];

// ─── Hooks ───────────────────────────────────────────────────────────────────

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function useCountUp(target: number, duration = 1800, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start: number | null = null;
    const raf = (ts: number) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }, [active, target, duration]);
  return count;
}

// ─── Reveal Wrapper ───────────────────────────────────────────────────────────

function Reveal({ children, delay = 0, fromY = 40, className = "" }: {
  children: React.ReactNode; delay?: number; fromY?: number; className?: string;
}) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : `translateY(${fromY}px)`,
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Counter Item ─────────────────────────────────────────────────────────────

function CounterItem({ end, suffix, label, icon, active }: {
  end: number; suffix: string; label: string; icon: string; active: boolean;
}) {
  const count = useCountUp(end, 1800, active);
  return (
    <div className="flex items-center gap-4 px-6 py-4 bg-white rounded-2xl border border-gray-100 group hover:-translate-y-0.5 transition-transform cursor-default">
      <div className="w-10 h-10 flex items-center justify-center bg-[#0EA5A0]/10 rounded-xl text-[#0EA5A0]">
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <div className="text-2xl font-extrabold text-[#1A2B3C] leading-none tabular-nums">
          {count.toLocaleString("zh")}<span className="text-[#0EA5A0]">{suffix}</span>
        </div>
        <div className="text-xs text-[#718096] mt-0.5">{label}</div>
      </div>
    </div>
  );
}

// ─── Feature Card ─────────────────────────────────────────────────────────────

function FeatureCard({ feature, delay }: { feature: typeof FEATURES[0]; delay: number }) {
  const [hovered, setHovered] = useState(false);
  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  return (
    <Reveal delay={delay} fromY={48}>
      <div
        className="bg-white rounded-2xl p-6 border border-transparent hover:border-[#0EA5A0]/30 transition-all duration-300 cursor-pointer relative overflow-hidden h-full flex flex-col"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ transform: hovered ? "translateY(-4px)" : "translateY(0)" }}
      >
        {/* Top accent bar */}
        <div
          className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${feature.gradient} transition-opacity duration-300`}
          style={{ opacity: hovered ? 1 : 0 }}
        />

        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 flex-shrink-0`}>
          <i className={`${feature.icon} text-white text-xl`} />
        </div>

        {/* Title */}
        <h3 className="text-base font-semibold text-[#1A2B3C] mb-2">{feature.title}</h3>

        {/* Description — fades out on hover */}
        <p
          className="text-[#718096] text-sm leading-relaxed transition-all duration-300"
          style={{ opacity: hovered ? 0 : 1, maxHeight: hovered ? 0 : "120px", overflow: "hidden" }}
        >
          {feature.desc}
        </p>

        {/* Detail bullets — slide in on hover */}
        <ul
          className="space-y-2 transition-all duration-300 overflow-hidden"
          style={{ maxHeight: hovered ? "200px" : "0px", opacity: hovered ? 1 : 0 }}
        >
          {feature.details.map((d, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-[#4A5568] leading-relaxed">
              <span className="mt-[3px] flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-[#0EA5A0]/15 text-[#0EA5A0]">
                <RIcon name="check-line" className="text-[10px]" />
              </span>
              {d}
            </li>
          ))}
        </ul>

        {/* Bottom arrow */}
        <div className="mt-auto pt-5 flex justify-end">
          <span
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all duration-300 ${
              hovered ? "bg-[#0EA5A0] text-white" : "bg-[#0EA5A0]/10 text-[#0EA5A0]"
            }`}
          >
            <RIcon name="arrow-right-line" className="text-sm" />
          </span>
        </div>
      </div>
    </Reveal>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Features() {
  const { ref: statsRef, inView: statsInView } = useInView(0.3);
  const [platformStats, setPlatformStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    getPlatformStats().then(setPlatformStats).catch(() => {});
  }, []);

  const STATS = buildFeatureStats(platformStats);

  return (
    <section id="features" className="py-20 bg-[#F4F8FB]">
      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <Reveal delay={0} fromY={30} className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5A0]" />
              <span className="text-[#0EA5A0] text-sm font-medium uppercase tracking-wider">核心能力</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A2B3C] leading-tight">
              <span className="text-[#0EA5A0]">四大核心能力</span>，
              <br className="hidden md:block" />
              覆盖全流程知识场景
            </h2>
          </div>
          <span className="flex-shrink-0 bg-[#1A2B3C] text-white text-xs px-3 py-1.5 rounded-full font-medium self-start md:self-auto">
            v2.0 正式版
          </span>
        </Reveal>

        {/* ── Animated Stats Row ── */}
        <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 80} fromY={24}>
              <CounterItem {...s} active={statsInView} />
            </Reveal>
          ))}
        </div>

        {/* ── Feature Cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, idx) => (
            <FeatureCard key={idx} feature={f} delay={idx * 80} />
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <Reveal delay={100} fromY={36} className="mt-14">
          <div className="bg-gradient-to-r from-[#0A4A47] to-[#0EA5A0] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <div className="flex-1">
              <h3 className="text-white text-2xl font-bold mb-2">准备好提升团队知识效率了吗？</h3>
              <p className="text-white/70 text-sm">立即登录系统，体验AI驱动的医疗知识管理新范式</p>
            </div>
            <a
              href="/login"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-[#AAFF45] text-[#1A2B3C] px-7 h-11 rounded-full text-sm font-semibold hover:bg-[#99EE35] transition-all whitespace-nowrap"
            >
              立即体验 <RIcon name="arrow-right-line" />
            </a>
          </div>
        </Reveal>

      </div>
    </section>
  );
}
